/**
 * Admin home — lists all managed items grouped by kind.
 * Only accessible when auth().is_admin === true.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { adminList, adminDelete, adminUpdate, type AdminItem } from "@/api";
import { useAuth } from "@/tg/AuthContext";
import { useI18n } from "@/i18n";
import BackBar from "@/components/BackBar";

function groupBy<T>(arr: T[], key: (item: T) => string): Record<string, T[]> {
  return arr.reduce<Record<string, T[]>>((acc, item) => {
    const k = key(item);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {});
}

export default function AdminHome() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { t } = useI18n();

  const [items, setItems] = useState<AdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guard: redirect non-admins
  useEffect(() => {
    if (auth !== null && !auth.is_admin) {
      navigate("/home", { replace: true });
    }
  }, [auth, navigate]);

  function load() {
    setLoading(true);
    adminList()
      .then((data) => {
        setItems(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Error");
        setLoading(false);
      });
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete(id: string) {
    if (!window.confirm(t("admin_confirm_delete"))) return;
    await adminDelete(id).catch(console.error);
    load();
  }

  async function handleTogglePublish(item: AdminItem) {
    await adminUpdate(item.id, { published: !item.published }).catch(console.error);
    load();
  }

  const grouped = groupBy(items, (i) => i.kind);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen pb-8"
      style={{ background: "var(--tg-bg)" }}
    >
      <BackBar to="/home" />

      <div className="px-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold" style={{ color: "var(--tg-text)" }}>Admin</h1>
          <button
            onClick={() => navigate("/admin/edit")}
            className="px-4 py-2 rounded-xl text-sm font-semibold"
            style={{ background: "var(--brand-blue)", color: "#ffffff", border: "none", cursor: "pointer" }}
          >
            + {t("admin_new")}
          </button>
        </div>

        {loading && <p style={{ color: "var(--tg-hint)" }}>{t("loading")}</p>}
        {error && <p style={{ color: "var(--danger)" }}>{error}</p>}

        {!loading && items.length === 0 && (
          <p style={{ color: "var(--tg-hint)" }}>{t("empty_state")}</p>
        )}

        {Object.entries(grouped).map(([kind, kindItems]) => (
          <section key={kind} className="mb-6">
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: "var(--tg-hint)" }}>
              {kind}
            </p>
            <div className="space-y-2">
              {kindItems.map((item) => (
                <div
                  key={item.id}
                  className="rounded-xl p-3 flex items-start gap-3"
                  style={{ background: "var(--tg-secondary-bg)" }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: "var(--tg-text)" }}>
                      {item.title}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--tg-hint)" }}>
                      {item.locale}
                    </p>
                  </div>

                  {/* Published badge */}
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-semibold shrink-0"
                    style={{
                      background: item.published ? "#22c55e22" : "var(--surface-muted)",
                      color: item.published ? "#16a34a" : "var(--tg-hint)",
                    }}
                  >
                    {item.published ? t("admin_published") : t("admin_draft")}
                  </span>

                  {/* Actions */}
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => handleTogglePublish(item)}
                      className="text-xs px-2 py-1 rounded-lg"
                      style={{ background: "var(--surface-muted)", border: "none", cursor: "pointer", color: "var(--tg-text)" }}
                    >
                      {item.published ? t("admin_unpublish") : t("admin_publish")}
                    </button>
                    <button
                      onClick={() => navigate(`/admin/edit/${item.id}`)}
                      className="text-xs px-2 py-1 rounded-lg"
                      style={{ background: "var(--brand-blue)", border: "none", cursor: "pointer", color: "#ffffff", fontWeight: 600 }}
                    >
                      {t("admin_edit")}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-xs px-2 py-1 rounded-lg"
                      style={{ background: "var(--danger)", border: "none", cursor: "pointer", color: "#fff" }}
                    >
                      {t("admin_delete")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </motion.div>
  );
}
